/**
 * SPSS 2026 Registration — Question Config Builder
 *
 * Transforms the raw Google Form JSON (spssQuestions.json) into a flat
 * questionsConfig array with branching conditions. The raw JSON is the
 * single source of truth for question titles, help text, and required status.
 *
 * Each question in the output adds:
 *   - A string `id` for form state management
 *   - A mapped `type` for the correct input component
 *   - Branching conditions (`parentId`/`parentValue` or `visible` fn)
 *   - Validation rules not present in the raw JSON
 */
import spssQuestions from "../../spssQuestions.json";

/* ── JSON lookup ─────────────────────────────────────────────────────────── */

const jsonById = new Map(spssQuestions.questions.map((q) => [q.id, q]));

/**
 * Create a config entry by referencing a raw JSON question ID.
 * Overrides are merged on top of the JSON-derived base fields.
 */
function fromJson(jsonId, overrides = {}) {
  const jq = jsonById.get(jsonId);
  if (!jq) throw new Error(`Question ID ${jsonId} not found in spssQuestions.json`);

  const base = {
    id: `q${jsonId}`,
    question: jq.title,
    subtext: jq.helpText || "",
    required: jq.required,
  };

  return { ...base, ...overrides };
}

/* ── Constants ───────────────────────────────────────────────────────────── */

const BINUS = "BINUSIAN";
const NON_BINUS = "NON - BINUSIAN";
const GLANCE = "GLANCE Competition";
const SMILE = "SMILE Competition";
const INDIVIDU = "Individu";
const KELOMPOK = "Kelompok";

const REGIONS = [
  { label: "Pilih region...", value: "" },
  { label: "Kemanggisan", value: "Kemanggisan" },
  { label: "Alam Sutera", value: "Alam Sutera" },
  { label: "Malang", value: "Malang" },
  { label: "Bandung", value: "Bandung" },
  { label: "Bekasi", value: "Bekasi" },
  { label: "Semarang", value: "Semarang" },
  { label: "Medan", value: "Medan" },
  { label: "Senayan", value: "Senayan" },
];

const BINUSIAN_UNIVERSITY = [
  { label: "Universitas Bina Nusantara", value: "Universitas Bina Nusantara" },
];

/* ── Input filtering & validation helpers ───────────────────────────────── */
// `filter` strips characters that clearly don't belong in a field as the user
// types. `validation.pattern` etc. is the final gate before proceeding.

const digitsOnly = (value) => value.replace(/[^0-9]/g, "");
const stripSpaces = (value) => value.replace(/\s+/g, "");
const nameChars = (value) => value.replace(/[^\p{L}\s'’.-]/gu, "");
const groupNameChars = (value) => value.replace(/[^\p{L}\p{N}\s'’&()/.-]/gu, "");
const wordChars = (value) => value.replace(/[^\p{L}\p{N}\s'’&(),/.-]/gu, "");

const NAME_PATTERN = /^[\p{L}][\p{L}\s'’.-]{1,}$/u;
const GROUP_NAME_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N}\s'’&()/.-]{1,}$/u;
const WORD_PATTERN = /^[\p{L}][\p{L}\p{N}\s'’&(),/.-]{1,}$/u;
const PHONE_PATTERN = /^8[1-9][0-9]{7,11}$/;

const NAME_VALIDATION = {
  pattern: NAME_PATTERN,
  patternMessage:
    "Nama hanya boleh berisi huruf, spasi, serta tanda baca seperti titik (.), apostrof (') atau tanda hubung (-).",
};

const GROUP_NAME_VALIDATION = {
  pattern: GROUP_NAME_PATTERN,
  patternMessage: "Nama kelompok tidak valid.",
  minLength: 2,
};

const JURUSAN_VALIDATION = {
  pattern: WORD_PATTERN,
  patternMessage: "Jurusan tidak valid. Hanya huruf, angka, dan spasi yang diperbolehkan.",
  minLength: 2,
};

const UNIVERSITAS_VALIDATION = {
  pattern: WORD_PATTERN,
  patternMessage: "Nama universitas tidak valid.",
  minLength: 2,
};

const BINUSIAN_NIM_VALIDATION = {
  pattern: /^29\d{8}$/,
  patternMessage: "NIM harus 10 digit angka dan dimulai dengan 29.",
  maxLength: 10,
};

const NON_BINUSIAN_NIM_VALIDATION = {
  pattern: /^\d+$/,
  patternMessage: "NIM hanya boleh berisi angka.",
  maxLength: 20,
};

const EMAIL_VALIDATION = {
  pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  patternMessage: "Email harus berupa email yang valid.",
};

const BINUSIAN_EMAIL_VALIDATION = {
  pattern: /^[^\s@]+@binus\.ac\.id$/,
  patternMessage: "Email harus berupa email BINUS yang valid (@binus.ac.id).",
};

const PHONE_VALIDATION = {
  pattern: PHONE_PATTERN,
  patternMessage:
    "Nomor WhatsApp tidak valid. Masukkan nomor setelah +62, contoh: 81234567890.",
  maxLength: 13,
};

/* ── Condition helpers ───────────────────────────────────────────────────── */

const isBinus = (fd) => fd.kategori_peserta === BINUS;
const isNonBinus = (fd) => fd.kategori_peserta === NON_BINUS;
const isGlanceBinus = (fd) => isBinus(fd) && fd.lomba_binus === GLANCE;
const isSmileBinus = (fd) => isBinus(fd) && fd.lomba_binus === SMILE;
const isGlanceNonBinus = (fd) => isNonBinus(fd) && fd.lomba_nonbinus === GLANCE;
const isSmileNonBinus = (fd) => isNonBinus(fd) && fd.lomba_nonbinus === SMILE;
const isGlance = (fd) => isGlanceBinus(fd) || isGlanceNonBinus(fd);
const isSmile = (fd) => isSmileBinus(fd) || isSmileNonBinus(fd);

const isSmileIndividuBinus = (fd) =>
  isSmileBinus(fd) && fd.jenis_daftar_binus === INDIVIDU;
const isSmileKelompokBinus = (fd) =>
  isSmileBinus(fd) && fd.jenis_daftar_binus === KELOMPOK;
const isSmileIndividuNonBinus = (fd) =>
  isSmileNonBinus(fd) && fd.jenis_daftar_nonbinus === INDIVIDU;
const isSmileKelompokNonBinus = (fd) =>
  isSmileNonBinus(fd) && fd.jenis_daftar_nonbinus === KELOMPOK;

/* ── Template helpers for repeated field groups ──────────────────────────── */

function personalDataBinusian(prefix, jsonPrefix) {
  return [
    fromJson(jsonPrefix.nama, {
      id: `${prefix}_nama`,
      section: "Data Diri",
      type: "text",
      placeholder: "Nama lengkap",
      filter: nameChars,
      validation: NAME_VALIDATION,
    }),
    fromJson(jsonPrefix.nim, {
      id: `${prefix}_nim`,
      section: "Data Diri",
      type: "text",
      placeholder: "29xxxxxxxx",
      filter: digitsOnly,
      validation: BINUSIAN_NIM_VALIDATION,
    }),
    fromJson(jsonPrefix.email, {
      id: `${prefix}_email`,
      section: "Data Diri",
      type: "email",
      placeholder: "xxx@binus.ac.id",
      filter: stripSpaces,
      validation: BINUSIAN_EMAIL_VALIDATION,
    }),
    fromJson(jsonPrefix.whatsapp, {
      id: `${prefix}_whatsapp`,
      section: "Data Diri",
      type: "phone",
      placeholder: "8xxxxxxxxxx",
      filter: digitsOnly,
      validation: PHONE_VALIDATION,
    }),
    fromJson(jsonPrefix.jurusan, {
      id: `${prefix}_jurusan`,
      section: "Data Diri",
      type: "text",
      placeholder: "Jurusan",
      filter: wordChars,
      validation: JURUSAN_VALIDATION,
    }),
    fromJson(jsonPrefix.universitas, {
      id: `${prefix}_universitas`,
      section: "Data Diri",
      type: "select",
      options: BINUSIAN_UNIVERSITY,
    }),
    fromJson(jsonPrefix.region, {
      id: `${prefix}_region`,
      section: "Data Diri",
      type: "select",
      options: REGIONS,
    }),
  ];
}

function personalDataNonBinusian(prefix, jsonPrefix) {
  return [
    fromJson(jsonPrefix.nama, {
      id: `${prefix}_nama`,
      section: "Data Diri",
      type: "text",
      placeholder: "Nama lengkap",
      filter: nameChars,
      validation: NAME_VALIDATION,
    }),
    fromJson(jsonPrefix.nim, {
      id: `${prefix}_nim`,
      section: "Data Diri",
      type: "text",
      placeholder: "NIM",
      filter: digitsOnly,
      validation: NON_BINUSIAN_NIM_VALIDATION,
    }),
    fromJson(jsonPrefix.email, {
      id: `${prefix}_email`,
      section: "Data Diri",
      type: "email",
      placeholder: "xxx@gmail.com",
      filter: stripSpaces,
      validation: EMAIL_VALIDATION,
    }),
    fromJson(jsonPrefix.whatsapp, {
      id: `${prefix}_whatsapp`,
      section: "Data Diri",
      type: "phone",
      placeholder: "8xxxxxxxxxx",
      filter: digitsOnly,
      validation: PHONE_VALIDATION,
    }),
    fromJson(jsonPrefix.jurusan, {
      id: `${prefix}_jurusan`,
      section: "Data Diri",
      type: "text",
      placeholder: "Jurusan",
      filter: wordChars,
      validation: JURUSAN_VALIDATION,
    }),
    fromJson(jsonPrefix.universitas, {
      id: `${prefix}_universitas`,
      section: "Data Diri",
      type: "text",
      placeholder: "Nama universitas",
      filter: wordChars,
      validation: UNIVERSITAS_VALIDATION,
    }),
  ];
}

function teamKetuaBinusian(prefix, jsonPrefix) {
  return [
    fromJson(jsonPrefix.nama, {
      id: `${prefix}_nama`,
      section: "Data Tim",
      type: "text",
      placeholder: "Nama lengkap ketua",
      filter: nameChars,
      validation: NAME_VALIDATION,
    }),
    fromJson(jsonPrefix.nim, {
      id: `${prefix}_nim`,
      section: "Data Tim",
      type: "text",
      placeholder: "29xxxxxxxx",
      filter: digitsOnly,
      validation: BINUSIAN_NIM_VALIDATION,
    }),
    fromJson(jsonPrefix.email, {
      id: `${prefix}_email`,
      section: "Data Tim",
      type: "email",
      placeholder: "xxx@binus.ac.id",
      filter: stripSpaces,
      validation: BINUSIAN_EMAIL_VALIDATION,
    }),
    fromJson(jsonPrefix.whatsapp, {
      id: `${prefix}_whatsapp`,
      section: "Data Tim",
      type: "phone",
      placeholder: "8xxxxxxxxxx",
      filter: digitsOnly,
      validation: PHONE_VALIDATION,
    }),
    fromJson(jsonPrefix.jurusan, {
      id: `${prefix}_jurusan`,
      section: "Data Tim",
      type: "text",
      placeholder: "Jurusan",
      filter: wordChars,
      validation: JURUSAN_VALIDATION,
    }),
    fromJson(jsonPrefix.universitas, {
      id: `${prefix}_universitas`,
      section: "Data Tim",
      type: "select",
      options: BINUSIAN_UNIVERSITY,
    }),
    fromJson(jsonPrefix.region, {
      id: `${prefix}_region`,
      section: "Data Tim",
      type: "select",
      options: REGIONS,
    }),
  ];
}

function teamAnggotaBinusian(prefix, jsonPrefix, required = true) {
  return [
    fromJson(jsonPrefix.nama, {
      id: `${prefix}_nama`,
      section: "Data Tim",
      type: "text",
      placeholder: "Nama lengkap anggota",
      required,
      filter: nameChars,
      validation: NAME_VALIDATION,
    }),
    fromJson(jsonPrefix.nim, {
      id: `${prefix}_nim`,
      section: "Data Tim",
      type: "text",
      placeholder: "29xxxxxxxx",
      required,
      filter: digitsOnly,
      validation: BINUSIAN_NIM_VALIDATION,
    }),
    fromJson(jsonPrefix.email, {
      id: `${prefix}_email`,
      section: "Data Tim",
      type: "email",
      placeholder: "xxx@binus.ac.id",
      required,
      filter: stripSpaces,
      validation: BINUSIAN_EMAIL_VALIDATION,
    }),
    fromJson(jsonPrefix.whatsapp, {
      id: `${prefix}_whatsapp`,
      section: "Data Tim",
      type: "phone",
      placeholder: "8xxxxxxxxxx",
      required,
      filter: digitsOnly,
      validation: PHONE_VALIDATION,
    }),
    fromJson(jsonPrefix.jurusan, {
      id: `${prefix}_jurusan`,
      section: "Data Tim",
      type: "text",
      placeholder: "Jurusan",
      required,
      filter: wordChars,
      validation: JURUSAN_VALIDATION,
    }),
    fromJson(jsonPrefix.universitas, {
      id: `${prefix}_universitas`,
      section: "Data Tim",
      type: "select",
      options: BINUSIAN_UNIVERSITY,
      required,
    }),
    fromJson(jsonPrefix.region, {
      id: `${prefix}_region`,
      section: "Data Tim",
      type: "select",
      options: REGIONS,
      required,
    }),
  ];
}

function teamKetuaNonBinusian(prefix, jsonPrefix) {
  return [
    fromJson(jsonPrefix.nama, {
      id: `${prefix}_nama`,
      section: "Data Tim",
      type: "text",
      placeholder: "Nama lengkap ketua",
      filter: nameChars,
      validation: NAME_VALIDATION,
    }),
    fromJson(jsonPrefix.nim, {
      id: `${prefix}_nim`,
      section: "Data Tim",
      type: "text",
      placeholder: "NIM",
      filter: digitsOnly,
      validation: NON_BINUSIAN_NIM_VALIDATION,
    }),
    fromJson(jsonPrefix.email, {
      id: `${prefix}_email`,
      section: "Data Tim",
      type: "email",
      placeholder: "xxx@gmail.com",
      filter: stripSpaces,
      validation: EMAIL_VALIDATION,
    }),
    fromJson(jsonPrefix.whatsapp, {
      id: `${prefix}_whatsapp`,
      section: "Data Tim",
      type: "phone",
      placeholder: "8xxxxxxxxxx",
      filter: digitsOnly,
      validation: PHONE_VALIDATION,
    }),
    fromJson(jsonPrefix.jurusan, {
      id: `${prefix}_jurusan`,
      section: "Data Tim",
      type: "text",
      placeholder: "Jurusan",
      filter: wordChars,
      validation: JURUSAN_VALIDATION,
    }),
    fromJson(jsonPrefix.universitas, {
      id: `${prefix}_universitas`,
      section: "Data Tim",
      type: "text",
      placeholder: "Nama universitas",
      filter: wordChars,
      validation: UNIVERSITAS_VALIDATION,
    }),
  ];
}

function teamAnggotaNonBinusian(prefix, jsonPrefix, required = true) {
  return [
    fromJson(jsonPrefix.nama, {
      id: `${prefix}_nama`,
      section: "Data Tim",
      type: "text",
      placeholder: "Nama lengkap anggota",
      required,
      filter: nameChars,
      validation: NAME_VALIDATION,
    }),
    fromJson(jsonPrefix.nim, {
      id: `${prefix}_nim`,
      section: "Data Tim",
      type: "text",
      placeholder: "NIM",
      required,
      filter: digitsOnly,
      validation: NON_BINUSIAN_NIM_VALIDATION,
    }),
    fromJson(jsonPrefix.email, {
      id: `${prefix}_email`,
      section: "Data Tim",
      type: "email",
      placeholder: "xxx@gmail.com",
      required,
      filter: stripSpaces,
      validation: EMAIL_VALIDATION,
    }),
    fromJson(jsonPrefix.whatsapp, {
      id: `${prefix}_whatsapp`,
      section: "Data Tim",
      type: "phone",
      placeholder: "8xxxxxxxxxx",
      required,
      filter: digitsOnly,
      validation: PHONE_VALIDATION,
    }),
    fromJson(jsonPrefix.jurusan, {
      id: `${prefix}_jurusan`,
      section: "Data Tim",
      type: "text",
      placeholder: "Jurusan",
      required,
      filter: wordChars,
      validation: JURUSAN_VALIDATION,
    }),
    fromJson(jsonPrefix.universitas, {
      id: `${prefix}_universitas`,
      section: "Data Tim",
      type: "text",
      placeholder: "Nama universitas",
      required,
      filter: wordChars,
      validation: UNIVERSITAS_VALIDATION,
    }),
  ];
}

/* ── Build the full config ───────────────────────────────────────────────── */

const questionsConfig = [
  // ═════════════════════════════════════════════════════════════════════════
  // SECTION: Kategori Peserta
  // ═════════════════════════════════════════════════════════════════════════

  fromJson(1166837833, {
    id: "kategori_peserta",
    section: "Kategori Peserta",
    type: "radio",
    options: [
      { label: "BINUSIAN", value: BINUS },
      { label: "NON - BINUSIAN", value: NON_BINUS },
    ],
  }),

  // ── BINUSIAN branch ──────────────────────────────────────────────────────

  fromJson(748247059, {
    id: "info_pemilihan_binus",
    section: "Kategori Peserta",
    type: "info",
    visible: isBinus,
  }),

  fromJson(1343432432, {
    id: "lomba_binus",
    section: "Kategori Peserta",
    type: "radio",
    visible: isBinus,
    options: [
      { label: "GLANCE Competition", value: GLANCE },
      { label: "SMILE Competition", value: SMILE },
    ],
  }),

  // ── NON-BINUSIAN branch ──────────────────────────────────────────────────

  fromJson(1101564970, {
    id: "info_pemilihan_nonbinus",
    section: "Kategori Peserta",
    type: "info",
    visible: isNonBinus,
  }),

  fromJson(1437244496, {
    id: "lomba_nonbinus",
    section: "Kategori Peserta",
    type: "radio",
    visible: isNonBinus,
    options: [
      { label: "GLANCE Competition", value: GLANCE },
      { label: "SMILE Competition", value: SMILE },
    ],
  }),

  // ═════════════════════════════════════════════════════════════════════════
  // SECTION: Data Diri — BINUSIAN → GLANCE
  // ═════════════════════════════════════════════════════════════════════════

  fromJson(759281352, {
    id: "info_data_glance_binus",
    section: "Data Diri",
    type: "info",
    visible: isGlanceBinus,
  }),

  ...personalDataBinusian("glance_binus", {
    nama: 1641495961,
    nim: 421722435,
    email: 989705462,
    whatsapp: 1367983509,
    jurusan: 1282451733,
    universitas: 400955677,
    region: 1382945047,
  }).map((q) => ({ ...q, visible: isGlanceBinus })),

  // ═════════════════════════════════════════════════════════════════════════
  // SECTION: Data Diri — BINUSIAN → SMILE
  // ═════════════════════════════════════════════════════════════════════════

  fromJson(821169450, {
    id: "info_data_smile_binus",
    section: "Data Diri",
    type: "info",
    visible: isSmileBinus,
  }),

  fromJson(1200392607, {
    id: "jenis_daftar_binus",
    section: "Data Diri",
    type: "radio",
    visible: isSmileBinus,
    options: [
      { label: "Individu", value: INDIVIDU },
      { label: "Kelompok", value: KELOMPOK },
    ],
  }),

  // ── BINUSIAN → SMILE → Individu ──────────────────────────────────────────

  fromJson(1295205586, {
    id: "info_smile_individu_binus",
    section: "Data Diri",
    type: "info",
    visible: isSmileIndividuBinus,
  }),

  ...personalDataBinusian("smile_individu_binus", {
    nama: 573823854,
    nim: 2037975598,
    email: 172261528,
    whatsapp: 1208282238,
    jurusan: 1032558337,
    universitas: 1094855098,
    region: 1572161112,
  }).map((q) => ({ ...q, visible: isSmileIndividuBinus })),

  // ── BINUSIAN → SMILE → Kelompok ──────────────────────────────────────────

  fromJson(911533234, {
    id: "info_smile_kelompok_binus",
    section: "Data Tim",
    type: "info",
    visible: isSmileKelompokBinus,
  }),

  fromJson(613826150, {
    id: "smile_kelompok_binus_nama",
    section: "Data Tim",
    type: "text",
    placeholder: "Nama kelompok",
    visible: isSmileKelompokBinus,
    filter: groupNameChars,
    validation: GROUP_NAME_VALIDATION,
  }),

  // Ketua
  ...teamKetuaBinusian("smile_kel_binus_ketua", {
    nama: 1250062170,
    nim: 1561941423,
    email: 615937901,
    whatsapp: 212938435,
    jurusan: 1549380294,
    universitas: 1559961276,
    region: 144422394,
  }).map((q) => ({ ...q, visible: isSmileKelompokBinus })),

  // Anggota 1
  ...teamAnggotaBinusian("smile_kel_binus_ang1", {
    nama: 2140100784,
    nim: 753073103,
    email: 2144510470,
    whatsapp: 367909181,
    jurusan: 1468063565,
    universitas: 677950796,
    region: 565641338,
  }).map((q) => ({ ...q, visible: isSmileKelompokBinus })),

  // Anggota 2 (optional)
  ...teamAnggotaBinusian("smile_kel_binus_ang2", {
    nama: 1778684628,
    nim: 1785027141,
    email: 766752305,
    whatsapp: 510790959,
    jurusan: 1132087671,
    universitas: 50548733,
    region: 1808494521,
  }, false).map((q) => ({ ...q, visible: isSmileKelompokBinus })),

  // ═════════════════════════════════════════════════════════════════════════
  // SECTION: Data Diri — NON-BINUSIAN → GLANCE
  // ═════════════════════════════════════════════════════════════════════════

  fromJson(1509867889, {
    id: "info_data_glance_nonbinus",
    section: "Data Diri",
    type: "info",
    visible: isGlanceNonBinus,
  }),

  ...personalDataNonBinusian("glance_nonbinus", {
    nama: 1391625565,
    nim: 1887769239,
    email: 32726489,
    whatsapp: 736642906,
    jurusan: 1599068796,
    universitas: 619414866,
  }).map((q) => ({ ...q, visible: isGlanceNonBinus })),

  // ═════════════════════════════════════════════════════════════════════════
  // SECTION: Data Diri — NON-BINUSIAN → SMILE
  // ═════════════════════════════════════════════════════════════════════════

  fromJson(1906672026, {
    id: "info_data_smile_nonbinus",
    section: "Data Diri",
    type: "info",
    visible: isSmileNonBinus,
  }),

  fromJson(394659614, {
    id: "jenis_daftar_nonbinus",
    section: "Data Diri",
    type: "radio",
    visible: isSmileNonBinus,
    options: [
      { label: "Individu", value: INDIVIDU },
      { label: "Kelompok", value: KELOMPOK },
    ],
  }),

  // ── NON-BINUSIAN → SMILE → Individu ──────────────────────────────────────

  fromJson(1069533428, {
    id: "info_smile_individu_nonbinus",
    section: "Data Diri",
    type: "info",
    visible: isSmileIndividuNonBinus,
  }),

  ...personalDataNonBinusian("smile_individu_nonbinus", {
    nama: 1835953986,
    nim: 1631886495,
    email: 2056736304,
    whatsapp: 2039621599,
    jurusan: 1887126914,
    universitas: 1365845662,
  }).map((q) => ({ ...q, visible: isSmileIndividuNonBinus })),

  // ── NON-BINUSIAN → SMILE → Kelompok ──────────────────────────────────────

  fromJson(1365836415, {
    id: "info_smile_kelompok_nonbinus",
    section: "Data Tim",
    type: "info",
    visible: isSmileKelompokNonBinus,
  }),

  fromJson(484225452, {
    id: "smile_kelompok_nonbinus_nama",
    section: "Data Tim",
    type: "text",
    placeholder: "Nama kelompok",
    visible: isSmileKelompokNonBinus,
    filter: groupNameChars,
    validation: GROUP_NAME_VALIDATION,
  }),

  // Ketua
  ...teamKetuaNonBinusian("smile_kel_nonbinus_ketua", {
    nama: 217806710,
    nim: 1649863164,
    email: 280231701,
    whatsapp: 862101032,
    jurusan: 745678794,
    universitas: 2119528098,
  }).map((q) => ({ ...q, visible: isSmileKelompokNonBinus })),

  // Anggota 1
  ...teamAnggotaNonBinusian("smile_kel_nonbinus_ang1", {
    nama: 1916267564,
    nim: 2068067335,
    email: 757586264,
    whatsapp: 1413556080,
    jurusan: 1331489509,
    universitas: 1788867285,
  }).map((q) => ({ ...q, visible: isSmileKelompokNonBinus })),

  // Anggota 2 (optional)
  ...teamAnggotaNonBinusian("smile_kel_nonbinus_ang2", {
    nama: 1699924519,
    nim: 974284824,
    email: 1253576897,
    whatsapp: 1281345283,
    jurusan: 1721791703,
    universitas: 887008973,
  }, false).map((q) => ({ ...q, visible: isSmileKelompokNonBinus })),

  // ═════════════════════════════════════════════════════════════════════════
  // Konfirmasi Ulang — appears at the end of Data Peserta for all paths
  // ═════════════════════════════════════════════════════════════════════════

  fromJson(814005247, {
    id: "info_konfirmasi_smile",
    section: "Data Diri",
    type: "info",
    visible: isSmile,
  }),

  fromJson(1422010227, {
    id: "info_konfirmasi_glance",
    section: "Data Diri",
    type: "info",
    visible: isGlance,
  }),

  // ═════════════════════════════════════════════════════════════════════════
  // SECTION: Upload Berkas — SMILE Competition
  // ═════════════════════════════════════════════════════════════════════════

  fromJson(56954857, {
    id: "ktm_smile",
    section: "Upload Berkas",
    type: "file",
    visible: isSmile,
    validation: {
      accept: [".pdf", ".jpg", ".jpeg", ".png"],
      maxSizeMB: 10,
    },
  }),

  fromJson(1800281411, {
    id: "twibbon_smile",
    section: "Upload Berkas",
    type: "file",
    visible: isSmile,
    validation: {
      accept: [".pdf", ".jpg", ".jpeg", ".png"],
      maxSizeMB: 10,
    },
  }),

  fromJson(1604081778, {
    id: "ig_story_smile",
    section: "Upload Berkas",
    type: "file",
    visible: isSmile,
    validation: {
      accept: [".pdf"],
      maxSizeMB: 10,
    },
  }),

  fromJson(1983931892, {
    id: "contact_person_smile",
    section: "Upload Berkas",
    type: "info",
    visible: isSmile,
  }),

  // ═════════════════════════════════════════════════════════════════════════
  // SECTION: Upload Berkas — GLANCE Competition
  // ═════════════════════════════════════════════════════════════════════════

  fromJson(927660233, {
    id: "ktm_glance",
    section: "Upload Berkas",
    type: "file",
    visible: isGlance,
    validation: {
      accept: [".jpg", ".jpeg", ".png"],
      maxSizeMB: 10,
    },
  }),

  fromJson(46386536, {
    id: "twibbon_glance",
    section: "Upload Berkas",
    type: "file",
    visible: isGlance,
    validation: {
      accept: [".jpg", ".jpeg", ".png"],
      maxSizeMB: 10,
    },
  }),

  fromJson(1980823842, {
    id: "ig_story_glance",
    section: "Upload Berkas",
    type: "file",
    visible: isGlance,
    validation: {
      accept: [".pdf"],
      maxSizeMB: 10,
    },
  }),

  fromJson(2096054522, {
    id: "contact_person_glance",
    section: "Upload Berkas",
    type: "info",
    visible: isGlance,
  }),

  // ═════════════════════════════════════════════════════════════════════════
  // SECTION: Pembayaran — SMILE Competition
  // ═════════════════════════════════════════════════════════════════════════

  fromJson(143967685, {
    id: "info_pembayaran_smile",
    section: "Pembayaran",
    type: "info",
    visible: isSmile,
  }),

  fromJson(1120830711, {
    id: "biaya_smile",
    section: "Pembayaran",
    type: "info",
    visible: isSmile,
  }),

  fromJson(1887560078, {
    id: "bukti_pembayaran_smile",
    section: "Pembayaran",
    type: "file",
    visible: isSmile,
    validation: {
      accept: [".jpg", ".jpeg", ".png", ".pdf"],
      maxSizeMB: 10,
    },
  }),

  fromJson(1010628085, {
    id: "guidebook_smile",
    section: "Pembayaran",
    type: "info",
    visible: isSmile,
  }),

  fromJson(887258205, {
    id: "persetujuan_smile",
    section: "Pembayaran",
    type: "radio",
    visible: isSmile,
    options: [{ label: "Setuju", value: "Setuju" }],
  }),

  // ═════════════════════════════════════════════════════════════════════════
  // SECTION: Pembayaran — GLANCE Competition
  // ═════════════════════════════════════════════════════════════════════════

  fromJson(669137079, {
    id: "info_pembayaran_glance",
    section: "Pembayaran",
    type: "info",
    visible: isGlance,
  }),

  fromJson(275284599, {
    id: "biaya_glance",
    section: "Pembayaran",
    type: "info",
    visible: isGlance,
  }),

  fromJson(374371979, {
    id: "bukti_pembayaran_glance",
    section: "Pembayaran",
    type: "file",
    visible: isGlance,
    validation: {
      accept: [".jpg", ".jpeg", ".png", ".pdf"],
      maxSizeMB: 10,
    },
  }),

  fromJson(1742025766, {
    id: "guidebook_glance",
    section: "Pembayaran",
    type: "info",
    visible: isGlance,
  }),

  fromJson(1461287483, {
    id: "persetujuan_glance",
    section: "Pembayaran",
    type: "radio",
    visible: isGlance,
    options: [{ label: "Setuju", value: "Setuju" }],
  }),
];

/* ── Mark questions that affect branching ────────────────────────────────── */
// Questions whose answer determines which subsequent questions appear.
// Used to decide whether to show a confirmation dialog when editing.

const BRANCHING_IDS = new Set([
  "kategori_peserta",
  "lomba_binus",
  "lomba_nonbinus",
  "jenis_daftar_binus",
  "jenis_daftar_nonbinus",
]);

const questionsConfigWithBranching = questionsConfig.map((q) => ({
  ...q,
  affectsBranching: BRANCHING_IDS.has(q.id),
}));

export default questionsConfigWithBranching;
